<style scoped>
.modal-content {
    width: min(1200px, 90vw);
}
</style>
<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h4 class="modal-title user-management-modal-title"><i class="icon icon-user align-text-bottom" aria-hidden="true"></i> {{ user.FirstName }} {{ user.LastName }}</h4>
            <button type="button" aria-label="Close" class="close icon icon-close" @click.prevent="close" />
        </div>
        <div class="modal-body">
            <dl>
                <dt>Email:</dt>
                <dd>{{ user.Email }}</dd>
                <br />
                <dt>Username:</dt>
                <dd>{{ user.UserName }}</dd>
            </dl>
            <div class="fixed-height-section" style="height: 350px">
                <ul class="list-group list-group-flush role-editor-list">
                    <RoleContainer :container="null" :root-code="containerData.ContainerCode" :depth="0" />
                </ul>
            </div>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-bare" @click.prevent="close">Close</button>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, provide, SetupContext } from "vue";
import { throwIfNonSuccessStatus, fetchXsrf } from "@/lib/fetchHelpers.3";
import RoleContainer from "./RoleContainer.3.vue";
import { userModalSetup, UserModalProps, RoleDtoExtended, GroupedRole } from "./UserModalMixin";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { useMainStore } from "@/mainStore";
import { activeContainerRolesInjectKey, implicitContainerRolesInjectKey, possibleContainerRolesInjectKey, getContainerLabelInjectKey, getRoleDisplayLabelInjectKey, setContainerRoleNamesInjectKey } from "./Users.3.vue.InjectKeys";
import { UserDto } from "@cs/UserDto";
import { UserRoleDto } from "@cs/UserRoleDto";
type Props = { userInput: Omit<UserDto, "Roles"> & { Roles: (UserRoleDto & { RoleDisplayLabel: string })[] } } & DialogProps & UserModalProps;

export default defineComponent({
    components: {
        RoleContainer,
    },
    props: ["userInput", "containerData", "savingCallback", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);
        const userModalMixin = userModalSetup(props);
        const mainStore = useMainStore();

        const user = ref(props.userInput);
        // const tab = ref("roles");

        const groupedRoles = computed(() => {
            const result = {} as Record<string, GroupedRole[]>;
            const possibleRoles = {} as Record<string, RoleDtoExtended[]>;
            (user.value.Roles || []).forEach(r => {
                const { ContainerCode: containerCode, RoleName: roleName, RoleDisplayLabel: roleDisplayLabel } = r;
                if (!(containerCode in result)) {
                    result[containerCode] = [];
                    possibleRoles[containerCode] = userModalMixin.possibleRoles(containerCode);
                }
                result[containerCode].push({
                    Container: containerCode,
                    ContainerLabel: userModalMixin.getContainerName(containerCode),
                    RoleName: roleName,
                    RoleDisplayLabel: roleDisplayLabel,
                    DirectAssignable: possibleRoles[containerCode].some(i => i.Name == roleName),
                });
            });
            return result;
        });

        const close = () => {
            emit("close");
        };
        // const addRole = async({ container, role }) => {
        //     await alterRoles({ [container]: [role] }, {});
        // };
        // const removeRole = async({ container, role }) => {
        //     await alterRoles({}, { [container]: [role] });
        // };
        const setContainerRoles = ({ container, roleNames }: { container: string; roleNames: string[] }) => {
            const possibleRoles = userModalMixin.possibleRoles(container).map(i => i.Name);
            const activeRoles = (groupedRoles.value[container] || []).map(i => i.RoleName!);
            const addingRoles = roleNames.filter(i => !activeRoles.includes(i));
            const removeRoles = activeRoles.filter(i => possibleRoles.includes(i) && !roleNames.includes(i));
            return alterRoles({ [container]: addingRoles }, { [container]: removeRoles });
        };
        const alterRoles = async (addingRoles: Record<string, string[]>, removingRoles: Record<string, string[]>) => {
            addingRoles = addingRoles || {};
            removingRoles = removingRoles || {};
            if (Object.values(addingRoles).flat().length == 0 && Object.values(removingRoles).flat().length == (user.value.Roles || []).length) {
                alert("You may not remove all groups and roles from a user. If you want to remove all access, delete the user.");
                throw new Error("UserDeleteBlocked");
            }
            const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersAlterUserRoles"), {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    userToken: user.value.UserToken,
                    addingRoles: addingRoles,
                    removingRoles: removingRoles,
                }),
            });
            throwIfNonSuccessStatus(response);
            user.value = await response.json();
            props.savingCallback(user.value);
        };

        provide(activeContainerRolesInjectKey, code => {
            const r = groupedRoles.value[code];
            return r == null ? [] : (r.filter(i => i.DirectAssignable) as unknown as RoleDtoExtended[]);
        });
        provide(implicitContainerRolesInjectKey, code => {
            return userModalMixin.subroles(Object.values(groupedRoles.value).flat() as RoleDtoExtended[])[code] || [];
        });
        provide(possibleContainerRolesInjectKey, code => {
            return userModalMixin.possibleRoles(code);
        });
        provide(getContainerLabelInjectKey, code => {
            return userModalMixin.getContainerName(code) ?? null;
        });
        provide(getRoleDisplayLabelInjectKey, (code, roleName) => {
            return userModalMixin.getRoleDisplayLabel(code, roleName);
        });
        provide(setContainerRoleNamesInjectKey, (code, roleNames) => {
            return setContainerRoles({ container: code, roleNames });
        });

        return {
            container: containerRef,
            user,
            close,
        };
    },
});
</script>
